import React, { useMemo } from 'react';
import { isDiscoveryBlockedHardcoded } from '@/utils/auth-utils';
import { checkOnboardingStep, getOnboardingData } from '../../utils/survey-utils';
import ImportInfluencerButton from '../common-components/OnboardingComponents/ImportInfluencerButton';
import LeftCollapse from '../Icons/icons/LeftCollapse';
import RightCollapse from '../Icons/icons/RightCollapse';
import SaralLogoOnly from '../Icons/icons/SaralLogoOnly';
import SaralLogoText from '../Icons/icons/SaralLogoText';
import { TransparentButton } from '../styledComponents';
import NavbarItem from './components/NavbarItem';
import './styles/_dashboard.scss';
import useChristmasLogo from '@/hooks/useChristmasLogo';
import useDiwaliLogo from '@/hooks/useDiwaliLogo';
import useUSALogo from '@/hooks/useUSALogo';
import SaralLogoOnlyUSA from '@/images/saral-logo-only-usa.svg';
import saralUSALogo from '@/images/saral-logo-usa.svg';
import ChristmasLogo from '@/images/saralChristmasLogo.svg';
import DiwaliLogo from '@/images/saralDiwaliLogo.svg';
import { SetShowSearchResults } from '@/redux/discovery/actions';
import { setSidebarEffectiveExpanded, setSidebarExpanded } from '@/redux/sidebar/sidebarActions';
import { SUBSCRIPTION_STATUS_ENUM } from '@Saral-Influencer-Marketing/saral-utils/src/constants';
import clsx from 'clsx';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import CustomTooltip from '../common-components/CustomTooltip/CustomTooltip';
import { SOCIAL_LISTENING_FEATURE_FLAG_NAME } from './SocialListening/constants';

const Sidebar = ({
  isOnboardingShown,
  onboardingStep,
  userDetails,
  applicationCount,
  isUserOnPreservationPlan,
  userOnLitePlan,
  forceCollapse,
  disableHoverExpand,
}) => {
  const dispatch = useDispatch();
  const { isExpanded, isEffectiveExpanded } = useSelector((state) => state.sidebar);
  const { unreadEmailsCount } = useSelector((state) => state.inbox);
  const socialListeningEnabled =
    import.meta.env.REACT_APP_SARAL_ENV === 'prod' ? useFeatureFlagEnabled(SOCIAL_LISTENING_FEATURE_FLAG_NAME) : true;

  const handleMouseEnter = () => {
    if (disableHoverExpand) return;
    dispatch(setSidebarEffectiveExpanded(true));
  };

  const handleMouseLeave = () => {
    dispatch(setSidebarEffectiveExpanded(forceCollapse ? false : isExpanded));
  };

  const onExpandChange = (expanded) => {
    dispatch(setSidebarExpanded(expanded));
    dispatch(setSidebarEffectiveExpanded(expanded));
  };

  const isTrialUser = userDetails?.subscriptionStatus === SUBSCRIPTION_STATUS_ENUM.trial;
  const showFlows =
    import.meta.env.REACT_APP_SARAL_ENV !== 'prod' ||
    ['646e65fd06be4bfc8326b2ec', '6ad5d57fb7d304d7d90f705c'].includes(userDetails?._id?.toString());

  const showUSALogo = useUSALogo();
  const showDiwaliLogo = useDiwaliLogo();
  const { showChristmasLogo } = useChristmasLogo();

  const isShowingFullLogo = useMemo(() => {
    return (!forceCollapse && (isExpanded || isEffectiveExpanded)) || (forceCollapse && isEffectiveExpanded);
  }, [isExpanded, isEffectiveExpanded, forceCollapse]);

  const getLogo = useMemo(() => {
    return isShowingFullLogo ? (
      <img src={saralUSALogo} alt="USA_logo" />
    ) : (
      <img src={SaralLogoOnlyUSA} alt="USA_logo" width={50} height={45} />
    );
  }, [isShowingFullLogo]);

  const { showTooltip, tooltipText } = useMemo(() => {
    if (showUSALogo) {
      return {
        showTooltip: true,
        tooltipText: 'Happy 4th of July!',
      };
    }

    if (showDiwaliLogo) {
      return {
        showTooltip: true,
        tooltipText: "It's Diwali, the festival of lights! 🪔",
      };
    }

    if (showChristmasLogo) {
      return {
        showTooltip: true,
        tooltipText: 'Merry Christmas & Happy New Year!🎄',
      };
    }

    return {
      showTooltip: false,
      tooltipText: '',
    };
  }, [showDiwaliLogo, showUSALogo, showChristmasLogo]);

  return (
    <div
      className={`dashboard-sidenav ${isExpanded ? 'expanded' : 'collapsed'} ${
        !isExpanded && isEffectiveExpanded ? 'hover-expanded' : !isEffectiveExpanded ? 'collapsed' : ''
      }`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}>
      <CustomTooltip
        show={showTooltip}
        title={tooltipText}
        PopperProps={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [10, -35],
              },
            },
          ],
        }}>
        <Link
          to="/"
          className={clsx('saral-logo-div', {
            'py-6 mx-auto px-2': !isEffectiveExpanded && !isExpanded && showUSALogo,
            'gap-0': showDiwaliLogo,
          })}>
          {showUSALogo ? (
            getLogo
          ) : (
            <>
              {showDiwaliLogo ? (
                <img src={DiwaliLogo} alt="Diwali_logo" />
              ) : showChristmasLogo ? (
                <img src={ChristmasLogo} width={60} height={44} alt="Christmas_logo" />
              ) : (
                <SaralLogoOnly />
              )}
              {isShowingFullLogo && <SaralLogoText />}
            </>
          )}
        </Link>
      </CustomTooltip>

      <div className="navbar-div">
        <div className="upper-section">
          <ImportInfluencerButton
            isExpanded={isEffectiveExpanded}
            showOnboarding={!isOnboardingShown && checkOnboardingStep('importCreators', onboardingStep)}
            onboardingData={getOnboardingData('importCreators')}
            isUserOnPreservationPlan={isUserOnPreservationPlan}
          />
          {isTrialUser && (
            <NavbarItem
              isUserOnPreservationPlan={isUserOnPreservationPlan}
              icon="⚡️"
              nav="get-started"
              title="Get Started"
              isExpanded={isEffectiveExpanded}
              showOnboarding={false}
              onboardingData={{}}
              active={['/get-started']}
            />
          )}

          <NavbarItem
            isUserOnPreservationPlan={isUserOnPreservationPlan}
            icon="🏠"
            nav=""
            title="SARAL Home"
            isExpanded={isEffectiveExpanded}
            showOnboarding={!isOnboardingShown && checkOnboardingStep('saralHome', onboardingStep)}
            onboardingData={getOnboardingData('saralHome')}
            active={!isTrialUser ? ['/get-started'] : []}
          />
          {!isDiscoveryBlockedHardcoded(userDetails) && (
            <NavbarItem
              isUserOnPreservationPlan={isUserOnPreservationPlan}
              icon="🔍"
              nav={'discovery'}
              title="Discovery"
              isExpanded={isEffectiveExpanded}
              showOnboarding={!isOnboardingShown && checkOnboardingStep('discovery', onboardingStep)}
              onboardingData={getOnboardingData('discovery')}
              onClick={() => {
                dispatch(SetShowSearchResults(false));
              }}
            />
          )}

          <NavbarItem
            isUserOnPreservationPlan={isUserOnPreservationPlan}
            icon="🤝"
            nav="relationship"
            title="Relationships"
            isExpanded={isEffectiveExpanded}
            showOnboarding={!isOnboardingShown && checkOnboardingStep('relationships', onboardingStep)}
            onboardingData={getOnboardingData('relationships')}
          />

          <NavbarItem
            isUserOnPreservationPlan={isUserOnPreservationPlan}
            icon="📄"
            title="Lists"
            nav="list?tab=active&page=1"
            isExpanded={isEffectiveExpanded}
            showOnboarding={!isOnboardingShown && checkOnboardingStep('lists', onboardingStep)}
            onboardingData={getOnboardingData('lists')}
            active={['/list', '/list/detail']}
          />

          <NavbarItem
            isUserOnPreservationPlan={isUserOnPreservationPlan}
            icon="✉️"
            title="Email Drips"
            nav={'email-drips'}
            isExpanded={isEffectiveExpanded}
            showOnboarding={!isOnboardingShown && checkOnboardingStep('emailDrips', onboardingStep)}
            onboardingData={getOnboardingData('emailDrips')}
            active={['/email-drips', '/email-templates']}
          />

          <NavbarItem
            isUserOnPreservationPlan={isUserOnPreservationPlan}
            icon="📪️"
            nav="inbox"
            title="Inbox"
            disabled={false}
            isExpanded={isEffectiveExpanded}
            showOnboarding={false}
            onboardingData={{}}
            active={[]}
            badgeCount={unreadEmailsCount || 0}
          />

          <NavbarItem
            isUserOnPreservationPlan={isUserOnPreservationPlan}
            icon="🙋‍♂️"
            title="Applications"
            nav="applications"
            isExpanded={isEffectiveExpanded}
            showOnboarding={!isOnboardingShown && checkOnboardingStep('applications', onboardingStep)}
            onboardingData={getOnboardingData('applications')}
            active={['/applications']}
            badgeCount={applicationCount?.pending || 0}
          />

          {showFlows && (
            <NavbarItem
              isUserOnPreservationPlan={isUserOnPreservationPlan}
              icon="⚡"
              title="Flows"
              nav="flows"
              isExpanded={isEffectiveExpanded}
              showOnboarding={false}
              onboardingData={{}}
              active={['/flows']}
              isNew
              newTagText="Beta"
            />
          )}

          <NavbarItem
            isUserOnPreservationPlan={isUserOnPreservationPlan}
            icon="📊"
            nav="performance"
            title="Performance"
            isExpanded={isEffectiveExpanded}
            showOnboarding={!isOnboardingShown && checkOnboardingStep('performance', onboardingStep)}
            onboardingData={getOnboardingData('performance')}
          />

          <NavbarItem
            isUserOnPreservationPlan={isUserOnPreservationPlan || userOnLitePlan}
            icon="🎥️"
            nav="pulse"
            title="PULSE"
            disabled={userOnLitePlan}
            isExpanded={isEffectiveExpanded}
            isBetaFeature
            betaExternalCondition={!socialListeningEnabled}
            tooltipText={userOnLitePlan && 'Upgrade to Business to unlock'}
            showOnboarding={false}
            onboardingData={{}}
            active={['/social/add', 'social/']}
          />

          <NavbarItem
            isUserOnPreservationPlan={isUserOnPreservationPlan}
            icon="💰"
            nav="payments"
            title="Payments"
            isExpanded={isEffectiveExpanded}
            showOnboarding={!isOnboardingShown && checkOnboardingStep('payments', onboardingStep)}
            onboardingData={getOnboardingData('payments')}
          />
        </div>
        <div className="bottom-setting">
          <NavbarItem
            isUserOnPreservationPlan={isUserOnPreservationPlan}
            icon="⚙️"
            title="Settings"
            nav="settings"
            showOnboarding={!isOnboardingShown && checkOnboardingStep('settings', onboardingStep)}
            onboardingData={getOnboardingData('settings')}
            active={['/blacklisted']}
            isExpanded={isEffectiveExpanded}>
            {false}
          </NavbarItem>
          <CollapseButton forceCollapse={forceCollapse} isExpanded={isExpanded} onExpandChange={onExpandChange} />
        </div>
      </div>
    </div>
  );
};

const CollapseButton = ({ isExpanded, forceCollapse, onExpandChange }) => {
  if (forceCollapse) return null;
  if (isExpanded) {
    return (
      <TransparentButton
        onClick={() => onExpandChange(false)}
        style={{
          justifyContent: 'flex-start',
          padding: '6px 8px',
          fontSize: '12px',
          fontWeight: 500,
        }}
        color="#BDBDBD">
        <LeftCollapse />
        Collapse
      </TransparentButton>
    );
  }

  return (
    <TransparentButton
      onClick={() => onExpandChange(true)}
      style={{
        padding: '6px 8px',
        fontSize: '12px',
        fontWeight: 500,
      }}
      color="#BDBDBD">
      <RightCollapse />
    </TransparentButton>
  );
};

export default Sidebar;
